/**
 * Returns a debounced function.
 * - `leading = true`: run immediately on the first call in a burst.
 * - Always executes once at the end of the burst (trailing call).
 */
export function debounce(func, delay, leading = false) {
  let timerId = null;

  return (...args) => {
    const shouldCallLeading = leading && timerId === null;

    if (shouldCallLeading) {
      func(...args);
    }

    clearTimeout(timerId);
    timerId = setTimeout(() => {
      // Skip the trailing call when there was only a single leading call.
      if (!shouldCallLeading) {
        func(...args);
      }
      timerId = null;
    }, delay);
  };
}

/**
 * Returns a throttled function that executes at most once per `delay`.
 * Includes a trailing call with the latest arguments.
 */
export function throttle(func, delay) {
  let timerId = null;
  let lastExecutedTime = 0;

  return (...args) => {
    const now = Date.now();
    const elapsed = now - lastExecutedTime;

    if (elapsed >= delay) {
      if (timerId) {
        clearTimeout(timerId);
        timerId = null;
      }

      func(...args);
      lastExecutedTime = now;
      return;
    }

    const remaining = delay - elapsed;
    clearTimeout(timerId);
    timerId = setTimeout(() => {
      func(...args);
      lastExecutedTime = Date.now();
      timerId = null;
    }, remaining);
  };
}

/** Dispatches a CustomEvent from `document`. */
export function dispatchEvent(eventName, detail) {
  document.dispatchEvent(new CustomEvent(eventName, { detail }));
}

/** Resolves once the DOM is fully parsed and ready. */
export function domReady() {
  return new Promise((resolve) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', resolve, { once: true });
      return;
    }

    resolve();
  });
}

/** Simple Promise-based timeout helper. */
export function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
